/**
 * Data layer for `automated-invoice-generation-publisher-advertiser`. Thin typed
 * fetch wrappers over /api/v1/automated-invoice-generation-publisher-advertiser.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type PartyRole = "publisher" | "advertiser";
export type InvoiceStatus = "draft" | "sent" | "paid" | "overdue";

export interface InvoiceRow {
	id: number;
	tenant_id: string;
	invoice_id: string;
	party_role: PartyRole;
	party_id: string;
	period_start: number;
	period_end: number;
	subtotal_cents: number;
	tax_cents: number;
	total_cents: number;
	currency: string;
	status: InvoiceStatus;
	created_at: number;
}

export interface ListInvoicesResponse {
	count: number;
	items: InvoiceRow[];
}

export interface InvoiceEnvelope {
	invoice: InvoiceRow;
	created?: boolean;
	changed?: boolean;
}

export interface GenerateInvoiceBody {
	role: PartyRole;
	party_id: string;
	period_start: number;
	period_end: number;
	tax_cents?: number;
	currency?: string;
}

export interface ListFilters {
	role?: PartyRole;
	status?: InvoiceStatus;
	party_id?: string;
	start?: number;
	end?: number;
}

const BASE = "/api/v1/automated-invoice-generation-publisher-advertiser";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function buildQuery(filters: ListFilters | undefined): string {
	if (!filters) return "";
	const params: string[] = [];
	if (filters.role) params.push(`role=${encodeURIComponent(filters.role)}`);
	if (filters.status) params.push(`status=${encodeURIComponent(filters.status)}`);
	if (filters.party_id) params.push(`party_id=${encodeURIComponent(filters.party_id)}`);
	if (filters.start !== undefined) params.push(`start=${filters.start}`);
	if (filters.end !== undefined) params.push(`end=${filters.end}`);
	return params.length > 0 ? `?${params.join("&")}` : "";
}

export async function list(
	filters?: ListFilters,
): Promise<ListInvoicesResponse> {
	const res = await fetch(`${BASE}${buildQuery(filters)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListInvoicesResponse>(res);
}

export async function getOne(invoiceId: string): Promise<InvoiceEnvelope> {
	const res = await fetch(`${BASE}/${encodeURIComponent(invoiceId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<InvoiceEnvelope>(res);
}

export async function generate(
	body: GenerateInvoiceBody,
): Promise<InvoiceEnvelope> {
	const res = await fetch(`${BASE}/generate`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<InvoiceEnvelope>(res);
}

export async function setStatus(
	invoiceId: string,
	status: InvoiceStatus,
): Promise<InvoiceEnvelope> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(invoiceId)}/status`,
		{
			method: "PATCH",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify({ status }),
		},
	);
	return parseOrThrow<InvoiceEnvelope>(res);
}

export const automatedInvoiceGenerationPublisherAdvertiserQueryKeys = {
	all: ["automated-invoice-generation-publisher-advertiser"] as const,
	list: (filters?: ListFilters) =>
		[
			"automated-invoice-generation-publisher-advertiser",
			"list",
			filters ?? {},
		] as const,
	detail: (id: string) =>
		["automated-invoice-generation-publisher-advertiser", id] as const,
};

// ---------------------------------------------------------------------------
// Data helpers (pure, test-friendly)
// ---------------------------------------------------------------------------

/** Sum of total_cents across a slice of invoices. */
export function invoiceTotalCents(rows: InvoiceRow[]): number {
	return rows.reduce((s, r) => s + (r.total_cents ?? 0), 0);
}

/** Filter invoices by status. Returns a new array. */
export function filterByStatus(
	rows: InvoiceRow[],
	status: InvoiceStatus | "all",
): InvoiceRow[] {
	if (status === "all") return rows.slice();
	return rows.filter((r) => r.status === status);
}

/** Filter invoices by role. Returns a new array. */
export function filterByRole(
	rows: InvoiceRow[],
	role: PartyRole | "all",
): InvoiceRow[] {
	if (role === "all") return rows.slice();
	return rows.filter((r) => r.party_role === role);
}

/** Format cents as "$12.34". */
export function formatCents(cents: number, currency = "USD"): string {
	const dollars = (cents / 100).toFixed(2);
	if (currency === "USD") return `$${dollars}`;
	return `${dollars} ${currency}`;
}

/** Unix seconds → YYYY-MM-DD (UTC). */
export function formatPeriod(unixSeconds: number): string {
	if (!Number.isFinite(unixSeconds) || unixSeconds <= 0) return "-";
	const d = new Date(unixSeconds * 1000);
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}
